import React, { useEffect } from 'react';
import { Translate } from 'core/translate';
import ViewControl from '../viewControl/ViewControl';
import useLargeBreakpoint from '../../../hooks/useLargeBreakpoint';
import CompareSorting from './compareSorting/CompareSorting';
import CompareCarousel from '../compareCarousel/CompareCarousel';
import { combineClassNames } from '../../../../core/css';
import CompareSearch from '../compareSearch/CompareSearch';

const CompareHeaderDisclosure = ({
  filterText,
  displayFilterCount,
  displayInTableFilter,
  driveTypeSectionEl,
}) => {
  return (
    <>
      {displayInTableFilter && driveTypeSectionEl}
      {displayFilterCount && filterText && (
        <div
          className="compare-header__filter-dialog"
          role="status"
          aria-live="assertive"
          aria-atomic="true">
          <div className="compare-header__filter-count">{filterText}</div>
        </div>
      )}
    </>
  );
};

const CompareHeaderFilters = (props) => {
  const {
    columnLimiter,
    limitColumns,
    onColumnFilterToggle,
    showViewControl,
    hideMSRPDisclosure = false,
    isScrolledNav,
    isScrolled,
    analyticsKey,
    itemDescription,
  } = props;
  return (
    <div className="compare-header__filters">
      {!isScrolledNav ? <CompareHeaderDisclosure {...props} /> : null}
      {showViewControl ? (
        <ViewControl
          hideFromTab={isScrolledNav ? !isScrolled : isScrolled}
          limitColumns={limitColumns}
          columnLimiter={columnLimiter}
          onToggle={onColumnFilterToggle}
          analyticsKey={analyticsKey}
          itemDescription={itemDescription}
        />
      ) : null}
      {!isScrolledNav && !hideMSRPDisclosure && (
        <div className="compare-header__disclosure">
          <Translate value="compare.carousel.header.disclosure.v3" />
        </div>
      )}
    </div>
  );
};

const CompareHeaderCarousels = (props) => {
  const {
    columnOptions,
    headers,
    analyticsKey,
    handleIndexChange,
    staticTable,
    isScrolledNav,
    isScrolled,
    itemDescription,
  } = props;
  return (
    <div className="compare-header__carousel-container">
      {columnOptions.map((columnOption, i) => {
        return (
          <CompareCarousel
            key={i}
            columnIndex={i}
            columnOption={columnOption}
            slideDataList={headers}
            onSlideChange={handleIndexChange}
            isStatic={staticTable}
            hideFromTab={isScrolledNav ? !isScrolled : isScrolled}
            isScrolledNav={isScrolledNav}
            analyticsKey={analyticsKey}
            itemDescription={itemDescription}
          />
        );
      })}
    </div>
  );
};

const CompareHeaderSortingControls = (props) => {
  const {
    isAtLeastLarge,
    columnOptions,
    headers,
    analyticsKey,
    onSort,
    isScrolled,
    isScrolledNav,
    searchTerm,
    searchSuggestions,
    onSearchTermChange,
    onSearchSuggestionSelection,
    renderSuggestion,
    isSearchFocused,
    setIsSearchFocused,
  } = props;
  return (
    <div className="compare-header__sorting">
      {isAtLeastLarge ? (
        <div className="grid-placeholder">
          <CompareSearch
            searchTerm={searchTerm}
            searchSuggestions={searchSuggestions.slice(0, 10)}
            onSearchTermChange={onSearchTermChange}
            onSearchSuggestionSelection={onSearchSuggestionSelection}
            renderSuggestion={renderSuggestion}
            isFocused={isSearchFocused && isScrolledNav == isScrolled}
            setIsFocused={setIsSearchFocused}
            analyticsKey={analyticsKey}
          />
        </div>
      ) : null}
      {columnOptions.map(({ activeIndex, isSorted }, i) => {
        const sortingAnalyticsKey =
          i < headers.length
            ? analyticsKey + ':' + headers[activeIndex].trimKey
            : analyticsKey;
        return (
          <CompareSorting
            key={i}
            className="compare-sorting__toggle-option"
            activeIndex={activeIndex}
            isSorted={isSorted}
            onSort={onSort}
            columnIndex={i}
            analyticsKey={sortingAnalyticsKey}
            hideFromTab={isScrolledNav ? !isScrolled : isScrolled}
            headers={headers}
          />
        );
      })}
    </div>
  );
};

const CompareHeaderNav = (props) => {
  const {
    displaySort,
    isScrolledNav,
    scrolledClass,
    isScrolled,
    isScrolledUp,
    className,
    isAtLeastLarge,
    searchTerm,
    searchSuggestions,
    onSearchTermChange,
    onSearchSuggestionSelection,
    renderSuggestion,
    isSearchFocused,
    setIsSearchFocused,
    analyticsKey,
  } = props;
  return (
    <div
      className={`${combineClassNames('compare-header', className)}${
        isScrolled && scrolledClass ? ` ${scrolledClass}` : ''
      }`}>
      <CompareHeaderFilters
        {...props}
        isScrolledNav={isScrolledNav}
        hideFromTab={isScrolled}
      />
      <CompareHeaderCarousels {...props} isScrolledNav={isScrolledNav} />
      {displaySort && (
        <CompareHeaderSortingControls
          {...props}
          isScrolledNav={isScrolledNav}
        />
      )}
      {displaySort && !isAtLeastLarge && (
        <CompareSearch
          searchTerm={searchTerm}
          searchSuggestions={searchSuggestions.slice(0, 6)}
          onSearchTermChange={onSearchTermChange}
          onSearchSuggestionSelection={onSearchSuggestionSelection}
          renderSuggestion={renderSuggestion}
          isHidden={isScrolledNav && isScrolled && isScrolledUp}
          isFocused={isSearchFocused && isScrolledNav == isScrolled}
          setIsFocused={setIsSearchFocused}
          analyticsKey={analyticsKey}
        />
      )}
    </div>
  );
};

const CompareHeader = (props) => {
  const isAtLeastLarge = useLargeBreakpoint();

  useEffect(() => {
    const compareHeaderEl = document.querySelector('.compare-header');
    const compareHeaderScrolled = document.querySelector(
      '.compare-header--scrolled-state'
    );
    const setScrolledStateWidth = () => {
      compareHeaderScrolled.style.width = `${
        compareHeaderEl.getBoundingClientRect().width ||
        document.documentElement.clientWidth
      }px`;
    };
    window.addEventListener('resize', setScrolledStateWidth);
    setScrolledStateWidth();

    return () => {
      window.removeEventListener('resize', setScrolledStateWidth);
    };
  }, [props.isScrolled]);

  return (
    <>
      <CompareHeaderNav
        {...props}
        isScrolledNav={false}
        isAtLeastLarge={isAtLeastLarge}
      />
      <CompareHeaderNav
        {...props}
        className="compare-header--scrolled-state"
        scrolledClass="compare-header--sticky-fadeIn"
        isScrolledNav={true}
        isAtLeastLarge={isAtLeastLarge}
      />
    </>
  );
};

export default CompareHeader;
